import { Trans, useTranslation } from 'react-i18next'

import { Logger } from '@ps/common-features/sentry/loggerService'
import { Stepper } from '@ps/ds/components/blocks/Stepper/Stepper'
import { Heading } from '@ps/ds/components/primitives/Heading/Heading'
import { Calendar, CheckCircle2, MapPinHouse, Truck } from 'lucide-react'
import { GROUPED_REQUEST_STATUS } from 'src/api/constants/request'
import { WORK_ORDER_SCHEDULE_TYPES } from 'src/api/constants/workOrder'
import { TrackingWorkOrderScheduling } from 'src/api/domains/service-tracking/serviceTracking.types'
import type { GroupedRequestStatus } from 'src/api/types/requests'
import { NS } from 'src/constants/i18n'
import { TimelineDescription } from 'src/features/request-timeline/components/TimelineDescription'
import { TIMELINE_PHASES } from 'src/features/request-timeline/constants/timeline'
import { getTimelinePhase } from 'src/features/request-timeline/utils/getTimelinePhase'
import { formatLocalizedDate } from 'src/utils/date/formatLocalizedDate'

export type RequestTimelineProps = {
  groupedRequestStatus: GroupedRequestStatus
  requestNumber?: number
  workOrderId?: number
  providerName?: string | null
  scheduleType?: TrackingWorkOrderScheduling['scheduleType']
  firstServiceDate?: TrackingWorkOrderScheduling['firstServiceDate']
  lastServiceDate?: TrackingWorkOrderScheduling['lastServiceDate']
}

const SEMIBOLD_COMPONENT = <strong className="font-semibold" />

export const RequestTimeline = ({
  groupedRequestStatus,
  requestNumber,
  workOrderId,
  providerName,
  scheduleType,
  firstServiceDate,
  lastServiceDate,
}: RequestTimelineProps) => {
  const { t, i18n } = useTranslation(NS.TRACKING)
  const language = i18n.language
  const hasProvider = !!providerName
  const isScheduled = scheduleType != null && scheduleType !== WORK_ORDER_SCHEDULE_TYPES.UNSCHEDULED
  let isMultiTrip = scheduleType === WORK_ORDER_SCHEDULE_TYPES.MULTI_DAY
  const firstDate =
    isScheduled && firstServiceDate ? formatLocalizedDate(firstServiceDate, language) : undefined
  const lastDate =
    isMultiTrip && lastServiceDate ? formatLocalizedDate(lastServiceDate, language) : undefined
  const completionServiceDate = isMultiTrip ? lastServiceDate : firstServiceDate
  let serviceDate =
    isScheduled && completionServiceDate
      ? formatLocalizedDate(completionServiceDate, language)
      : undefined
  const phase = getTimelinePhase(
    groupedRequestStatus,
    scheduleType ?? WORK_ORDER_SCHEDULE_TYPES.UNSCHEDULED,
    hasProvider,
  )
  const isCompleted = phase === TIMELINE_PHASES.COMPLETED

  if (isCompleted && isMultiTrip && !lastServiceDate) {
    Logger.error('Completed multi-day request missing last service date', {
      context: {
        requestNumber,
        workOrderId,
        providerName,
        firstServiceDate,
      },
    })
    isMultiTrip = false
    serviceDate = firstDate
  }

  const steps = [
    {
      label: t('timeline.requestReceived.title'),
      description: t(
        hasProvider ? 'timeline.requestReceived.completed' : 'timeline.requestReceived.pending',
      ),
      icon: <CheckCircle2 />,
    },
    {
      label: t('timeline.providerAssigned.title'),
      description: hasProvider ? (
        <Trans
          i18nKey={`${NS.TRACKING}:timeline.providerAssigned.completed`}
          values={{ name: providerName }}
          components={{ strong: SEMIBOLD_COMPONENT }}
        />
      ) : (
        t('timeline.providerAssigned.pending')
      ),
      icon: <Truck />,
    },
    {
      label: t('timeline.serviceScheduled.title'),
      description: (
        <TimelineDescription
          providerName={providerName}
          isScheduled={isScheduled}
          isMultiTrip={isMultiTrip}
          isCompleted={isCompleted}
          firstDate={firstDate}
          lastDate={lastDate}
        />
      ),
      icon: <Calendar />,
    },
    {
      label: t('timeline.serviceCompleted.title'),
      description: isCompleted ? (
        <Trans
          i18nKey={`${NS.TRACKING}:timeline.serviceCompleted.completed`}
          values={{ date: serviceDate }}
          components={{ strong: SEMIBOLD_COMPONENT }}
        />
      ) : (
        t('timeline.serviceCompleted.pending')
      ),
      icon: <MapPinHouse />,
    },
  ]

  return (
    <section className="flex flex-col gap-4">
      <Heading as="h2" size="lg" weight="bold">
        {t('timeline.title')}
      </Heading>
      <Stepper
        steps={steps}
        activeIndex={Object.values(TIMELINE_PHASES).indexOf(phase)}
        disabled={groupedRequestStatus === GROUPED_REQUEST_STATUS.VOIDED}
      />
    </section>
  )
}
